import React, { FC, useMemo } from 'react'
import { AccordionCheckbox, AccordionItem, AccordionWrapper } from 'components/Accordion'
import { FormLabel } from 'components/Label'
import { Headline, Description, SubDescription } from 'components/Typography'
import { useStockAmountOptions } from 'hooks/stock'
import { Card, CardInner } from 'components/Card'
import {
  Checkbox,
  HoursToDeliveryDeadlineForm,
  RadioButton,
  SelectBox,
  TagCheckbox,
  Textarea,
  TextInput,
} from 'components/Form'
import { HorizontalLine, Spacer } from 'components/Utility'
import { UseStoreDeliveryBread } from 'hooks/storeDeliveryBread/useStoreDeliveryBread'
import { FlexRow } from 'components/Flex'
import { DeleteIconButton, TextButton } from 'components/Button'
import { PackageSize } from 'types/storeDeliveryFee'
import { useDeliveryCompanyOptions } from 'hooks/masterData'
import { useFetchDeliveryFees } from 'hooks/storeDeliveryFee'

type Props = {
  isChecked: boolean
  label: string
} & UseStoreDeliveryBread

const ProductEditDelivery: FC<Props> = (props) => {
  const {
    addChoice,
    choices,
    clearChoice,
    deleteChoice,
    deliveryCompany,
    frozenDeliveryType,
    handleChoice,
    handleFrozenDeliveryType,
    handleIsFrozenDelivery,
    handleHowToEat,
    handleHowToStore,
    hoursToDeadline,
    howToEat,
    howToStore,
    initChoice,
    isChecked,
    isFrozenDelivery,
    label,
    maximumOrder,
    packageSize,
    selectDeliveryCompany,
    selectMaximumOrder,
    selectPackageSize,
    selectDateToDeadline,
    selectTimeToDeadline,
    toggleCheckbox,
  } = props

  const deliverCompanyOptions = useDeliveryCompanyOptions()
  const maximumOrderOptions = useStockAmountOptions()
  const storeDeliveryFees = useFetchDeliveryFees()

  const packageSizeOptions = useMemo(() => {
    const options = [{ label: '未選択', value: '' }]
    storeDeliveryFees.map((storeDeliveryFee) => {
      options.push({ label: `${storeDeliveryFee.package_size}サイズ`, value: storeDeliveryFee.package_size })
    })
    return options
  }, [storeDeliveryFees])

  return (
    <AccordionWrapper>
      <AccordionCheckbox id={'delivery'} label={label} isChecked={isChecked} toggleCheckbox={toggleCheckbox}>
        <AccordionItem>
          <Spacer size={16} />
          <Headline headline={`配送設定`} />
          <Spacer size={16} />
          <Description description={'特定の日だけ販売数を変更するなど、後から編集も可能です。'} />
          <Spacer size={28} />
          <FormLabel id={`delivery-stock`} label={'1日に注文を受け付ける販売上限数'} required={true} />
          <Spacer size={8} />
          <SubDescription description={'１日に配送できる在庫上限数を設定してください。'} />
          <Spacer size={8} />
          <SelectBox options={maximumOrderOptions} select={selectMaximumOrder} value={maximumOrder} />
          <Spacer size={28} />
          <FormLabel label={'配送サイズ'} required={true} />
          <Spacer size={8} />
          <SubDescription
            description={`商品のサイズを選択してください。店舗で扱う配送サイズと配送料は、<a href="/delivery/setting/fee" target="_blank">配送料設定のページ</a>から編集できます。`}
          />
          <Spacer size={8} />
          <SelectBox<PackageSize | ''> options={packageSizeOptions} select={selectPackageSize} value={packageSize} />
          <Spacer size={28} />
          <FormLabel label={'配送会社/クール便の有無'} required={true} />
          <Spacer size={8} />
          <SubDescription
            description={'使用する配送会社を選択してください。お客さまが配送希望時刻を登録するために必要です。'}
          />
          <Spacer size={8} />
          <SelectBox options={deliverCompanyOptions} select={selectDeliveryCompany} value={deliveryCompany} />
          <Spacer size={28} />
          <Checkbox
            id={'is_frozen'}
            isChecked={isFrozenDelivery}
            label={'クール便を利用する'}
            onChange={handleIsFrozenDelivery}
            size={'large'}
          />
          <Spacer size={28} />
          {isFrozenDelivery && (
            <>
              <FlexRow justifyContent={'start'} wrap={'wrap'}>
                <TagCheckbox
                  hasHashtag={false}
                  id={'frozen'}
                  isChecked={frozenDeliveryType === 'frozen'}
                  onChange={handleFrozenDeliveryType}
                  type={'outlined'}
                  value={'クール便（冷凍）'}
                />
                <Spacer size={8} />
                <TagCheckbox
                  hasHashtag={false}
                  id={'chilled'}
                  isChecked={frozenDeliveryType === 'chilled'}
                  onChange={handleFrozenDeliveryType}
                  type={'outlined'}
                  value={'クール便（冷蔵）'}
                />
              </FlexRow>
              <Spacer size={28} />
            </>
          )}
          <FormLabel label={'注文締め切り'} required={true} />
          <Spacer size={8} />
          <SubDescription
            description={
              'お客さまが、アプリ上で最短のお届け日として選べる日時を設定します。注文が入ってから配送できる時間を予め確保してください。'
            }
          />
          <Spacer size={8} />
          <HoursToDeliveryDeadlineForm
            hoursToDeadline={hoursToDeadline}
            selectDateToDeadline={selectDateToDeadline}
            selectTimeToDeadline={selectTimeToDeadline}
          />
          <Spacer size={28} />
          <FormLabel label={'お召し上がり方'} required={false} />
          <Spacer size={8} />
          <SubDescription description={'お店おすすめの食べ方をご記入ください。'} />
          <Spacer size={8} />
          <Textarea
            disabled={false}
            maxLength={100}
            onChange={handleHowToEat}
            placeholder={'500Wで2分間温めてお召し上がりください'}
            required={false}
            rows={5}
            value={howToEat}
          />
          <Spacer size={28} />
          <FormLabel label={'保存方法'} required={false} />
          <Spacer size={8} />
          <SubDescription description={'商品の保存方法をご記入ください。'} />
          <Spacer size={8} />
          <Textarea
            disabled={false}
            maxLength={100}
            onChange={handleHowToStore}
            placeholder={'冷凍で1ヶ月保存可能です'}
            required={false}
            rows={5}
            value={howToStore}
          />
          <Spacer size={28} />
          <FormLabel label={'商品の受渡しオプション'} required={false} />
          <Spacer size={8} />
          <SubDescription
            description={'食パンのスライスなど、お客さまが購入する際に選択できるオプションがある場合設定してください。'}
          />
          <Spacer size={8} />
          <Card isOutlined={true}>
            <CardInner>
              <RadioButton
                isChecked={choices.length > 0}
                label={'受渡しオプション有り'}
                onChange={initChoice}
                value={'has_option_delivery'}
              />
            </CardInner>
            <HorizontalLine color={'grey03'} verticalMargin={16} />
            <CardInner>
              {choices.map((choice, index) => (
                <React.Fragment key={index.toString()}>
                  <FlexRow justifyContent={'start'}>
                    <TextInput
                      disabled={false}
                      onChange={(event) => handleChoice(event, index)}
                      placeholder={'4枚切り'}
                      required={false}
                      size={'small'}
                      type={'text'}
                      value={choice}
                    />
                    <Spacer size={8} />
                    <DeleteIconButton disabled={choices.length === 1} onClick={() => deleteChoice(index)} />
                  </FlexRow>
                  <Spacer size={16} />
                </React.Fragment>
              ))}
              <FlexRow justifyContent={'center'}>
                {choices.length === 0 || choices.some((choice) => choice === '') ? (
                  <TextButton
                    disabled={true}
                    iconPath={'/icon/grey/plus.svg'}
                    label={'オプションを追加する'}
                    onClick={addChoice}
                  />
                ) : (
                  <TextButton iconPath={'/icon/blue/plus.svg'} label={'オプションを追加する'} onClick={addChoice} />
                )}
              </FlexRow>
            </CardInner>
          </Card>
          <Spacer size={8} />
          <Card isOutlined={true}>
            <CardInner>
              <RadioButton
                isChecked={choices.length === 0}
                label={'受渡しオプション無し'}
                onChange={clearChoice}
                value={'no_option_delivery'}
              />
            </CardInner>
          </Card>
          <Spacer size={16} />
        </AccordionItem>
      </AccordionCheckbox>
    </AccordionWrapper>
  )
}

export default ProductEditDelivery
